import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import InventoryIcon from '@mui/icons-material/Inventory';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PermissionWrapper from '../components/PermissionWrapper';
import {
  armDeliveryPoller,
  fetchIntegrationStatus,
  runDeliveryPoller,
  runProductSync,
  runReconciliation,
  runStockPublish,
  type CombinedIntegrationStatus,
  type ReconciliationDifferenceItem,
} from '../services/integrationService';
import type { IntegrationScheduleEntry } from '../types/integration';

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('sv-SE');
}

function toDatetimeLocalValue(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function isoToDatetimeLocal(iso: string | null | undefined): string {
  if (!iso) return toDatetimeLocalValue(new Date());
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? toDatetimeLocalValue(new Date())
    : toDatetimeLocalValue(date);
}

function datetimeLocalToIso(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    throw new Error('Ogiltigt startdatum');
  }
  return date.toISOString();
}

function StatusChip({ value, unit = 'st' }: { value: number; unit?: string }) {
  if (value <= 0) {
    return <Chip size="small" color="success" label="OK" />;
  }
  return <Chip size="small" color="warning" label={`${value} ${unit}`} />;
}

function MetricRow({
  label,
  help,
  children,
}: {
  label: string;
  help?: string;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 2,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography color="text.secondary" variant="body2">
            {label}
          </Typography>
          {help ? (
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ display: 'block', mt: 0.25, lineHeight: 1.4 }}
            >
              {help}
            </Typography>
          ) : null}
        </Box>
        <Box sx={{ flexShrink: 0, textAlign: 'right' }}>{children}</Box>
      </Box>
    </Box>
  );
}

function IntegrationCardHeader({
  icon,
  title,
  description,
  schedules,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  schedules?: IntegrationScheduleEntry[];
}) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
        {icon}
        <Typography variant="h6">{title}</Typography>
      </Stack>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ lineHeight: 1.5 }}
      >
        {description}
      </Typography>
      {schedules && schedules.length > 0 ? (
        <Box
          sx={{
            mt: 1.5,
            p: 1.5,
            borderRadius: 1.5,
            bgcolor: 'action.hover',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography
              variant="caption"
              fontWeight={600}
              color="text.secondary"
            >
              Automatisk körning
            </Typography>
          </Stack>
          <Stack spacing={0.75}>
            {schedules.map((entry) => (
              <Box
                key={entry.label}
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 0.25, sm: 1 },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'flex-start', sm: 'baseline' },
                }}
              >
                <Typography
                  variant="caption"
                  color={
                    entry.enabled === false ? 'text.disabled' : 'text.secondary'
                  }
                  sx={{ fontWeight: 500, minWidth: { sm: 180 } }}
                >
                  {entry.label}
                </Typography>
                <Typography
                  variant="caption"
                  color={
                    entry.enabled === false ? 'text.disabled' : 'text.primary'
                  }
                  sx={{ textAlign: { sm: 'right' } }}
                >
                  {entry.enabled === false
                    ? `${entry.frequency} (avstängd)`
                    : entry.frequency}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      ) : null}
    </Box>
  );
}

function FlowStep({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        px: 1.5,
        py: 0.75,
        borderRadius: 1,
        bgcolor: 'action.hover',
        typography: 'body2',
        fontWeight: 500,
      }}
    >
      {children}
    </Box>
  );
}

const Integrations: React.FC = () => {
  const [status, setStatus] = useState<CombinedIntegrationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning';
  }>({ open: false, message: '', severity: 'success' });
  const [armDialogOpen, setArmDialogOpen] = useState(false);
  const [armStartAt, setArmStartAt] = useState(() =>
    toDatetimeLocalValue(new Date()),
  );
  const [reconciliationPreview, setReconciliationPreview] = useState<{
    runId: number;
    differences: ReconciliationDifferenceItem[];
  } | null>(null);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchIntegrationStatus();
      setStatus(data);
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error instanceof Error
            ? error.message
            : 'Kunde inte hämta integrationsstatus',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const runAction = async (
    key: string,
    action: () => Promise<unknown>,
    successMessage: string,
  ) => {
    setActionLoading(key);
    try {
      await action();
      setSnackbar({ open: true, message: successMessage, severity: 'success' });
      await loadStatus();
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error instanceof Error ? error.message : 'Åtgärden misslyckades',
        severity: 'error',
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleRunReadOnlyReconciliation = async () => {
    setActionLoading('recon');
    try {
      const result = await runReconciliation(undefined, 'read_only');
      await loadStatus();
      setReconciliationPreview({
        runId: result.runId,
        differences: result.differences ?? [],
      });
      setSnackbar({
        open: true,
        message:
          result.differenceCount === 0
            ? 'Kontrollsynk klar — inga avvikelser'
            : `Kontrollsynk klar — ${result.differenceCount} avvikelse${result.differenceCount === 1 ? '' : 'r'}`,
        severity: result.differenceCount === 0 ? 'success' : 'warning',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error instanceof Error
            ? error.message
            : 'Kontrollsynken misslyckades',
        severity: 'error',
      });
    } finally {
      setActionLoading(null);
    }
  };

  const mirakel = status?.mirakel;
  const stocknox = status?.stocknox;
  const deliveryArmed = mirakel?.delivery.armed ?? false;
  const deliveryPollerEnabled = mirakel?.delivery.pollerEnabled ?? false;

  const stockPublishSchedules = [
    ...(stocknox?.schedules?.fortnoxStockSync ?? []),
    ...(stocknox?.schedules?.stockPublish ?? []),
  ];
  const deliverySchedules = mirakel?.schedules?.delivery ?? [];
  const reconciliationSchedules = [
    ...(stocknox?.schedules?.reconciliation ?? []),
    ...(mirakel?.schedules?.reconciliation ?? []),
  ];
  const productSyncSchedules = stocknox?.schedules?.productSync ?? [];

  const handleOpenArmDialog = () => {
    setArmStartAt(isoToDatetimeLocal(mirakel?.delivery.startAfter));
    setArmDialogOpen(true);
  };

  const handleArmDeliveryPoller = async () => {
    setArmDialogOpen(false);
    setActionLoading('arm-delivery');
    try {
      const startAt = datetimeLocalToIso(armStartAt);
      const result = await armDeliveryPoller(startAt);
      setSnackbar({
        open: true,
        message: deliveryArmed
          ? `Starttid uppdaterad till ${formatDate(result.armedAt)}. Endast ordrar med orderdatum från och med detta bearbetas.`
          : `Utleveranser aktiverade från ${formatDate(result.armedAt)}. Ordrar med äldre orderdatum ignoreras.`,
        severity: 'success',
      });
      await loadStatus();
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error instanceof Error
            ? error.message
            : 'Kunde inte aktivera delivery-poller',
        severity: 'error',
      });
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <PermissionWrapper requiredPermissions={['manage_companies']}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography variant="h4" fontWeight={700}>
              Integrationer
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
              Övervakning och manuella körningar för Viskan ↔ Fortnox
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={
              loading ? <CircularProgress size={16} /> : <RefreshIcon />
            }
            onClick={loadStatus}
            disabled={loading}
          >
            Uppdatera
          </Button>
        </Stack>

        {loading && !status ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3}>
            <Alert
              severity="info"
              icon={<InfoOutlinedIcon />}
              sx={{ '& .MuiAlert-message': { width: '100%' } }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Så hänger systemen ihop
              </Typography>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                flexWrap="wrap"
                useFlexGap
                sx={{ mb: 1.5 }}
              >
                <FlowStep>Fortnox (lager)</FlowStep>
                <ArrowForwardIcon
                  fontSize="small"
                  color="action"
                  sx={{ display: { xs: 'none', sm: 'block' } }}
                />
                <FlowStep>Stocknox</FlowStep>
                <ArrowForwardIcon
                  fontSize="small"
                  color="action"
                  sx={{ display: { xs: 'none', sm: 'block' } }}
                />
                <FlowStep>Mirakel</FlowStep>
                <ArrowForwardIcon
                  fontSize="small"
                  color="action"
                  sx={{ display: { xs: 'none', sm: 'block' } }}
                />
                <FlowStep>Viskan (lager)</FlowStep>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Utleveranser går åt andra hållet: Viskan → Mirakel → Stocknox →
                Fortnox. Lager och utleveranser är separata flöden — du kan
                synka lager utan att aktivera utleveranser.
              </Typography>
            </Alert>

            {status && status.stocknoxReachable === false && (
              <Alert severity="warning">
                Mirakel når inte Stocknox API (
                {status.stocknoxError || 'timeout eller fel port'}). Kolla att
                Stocknox backend körs och att <code>STOCKNOX_BASE_URL</code> i
                Mirakel pekar på rätt port (t.ex.{' '}
                <code>http://localhost:3002</code>).
              </Alert>
            )}

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card
                  elevation={0}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    height: '100%',
                  }}
                >
                  <CardContent>
                    <IntegrationCardHeader
                      icon={<InventoryIcon color="primary" />}
                      title="Lagerpublicering"
                      description="Håller Viskans lagersaldo i synk med Fortnox. Fortnox är källan — när saldo ändras i Fortnox skickas det vidare till Viskan via Stocknox och Mirakel."
                      schedules={stockPublishSchedules}
                    />
                    <Stack spacing={2}>
                      <MetricRow
                        label="Senast publicerat till Viskan"
                        help="När Mirakel senast skrev uppdaterade saldon till Viskan."
                      >
                        <Typography variant="body2">
                          {formatDate(
                            mirakel?.stockPublish.lastSuccessfulPublishAt,
                          )}
                        </Typography>
                      </MetricRow>
                      <MetricRow
                        label="Väntar på publicering"
                        help="Antal saldoändringar i Stocknox som ännu inte skickats till Viskan. OK = 0."
                      >
                        <StatusChip
                          value={stocknox?.stockPublish?.pending || 0}
                          unit="rader"
                        />
                      </MetricRow>
                      <MetricRow
                        label="Äldsta väntande ändring"
                        help="Hur länge den äldsta opublicerade saldoändringen legat i kö. Tomt = inget väntar."
                      >
                        <Typography variant="body2">
                          {formatDate(stocknox?.stockPublish?.oldestPendingAt)}
                        </Typography>
                      </MetricRow>
                      <MetricRow
                        label="Misslyckade publiceringar"
                        help="Saldon som inte kunde skickas till Viskan efter flera försök. Kräver manuell felsökning om > 0."
                      >
                        <StatusChip
                          value={
                            (mirakel?.stockPublish.deadLetterCount || 0) +
                            (stocknox?.stockPublish?.deadLetters || 0)
                          }
                          unit="fel"
                        />
                      </MetricRow>
                    </Stack>
                    <Divider sx={{ my: 2 }} />
                    <Tooltip title="Tvingar Stocknox att skicka väntande saldoändringar till Mirakel/Viskan nu. Använd om kön inte töms av sig själv.">
                      <span>
                        <Button
                          variant="contained"
                          startIcon={
                            actionLoading === 'stock' ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <PlayArrowIcon />
                            )
                          }
                          disabled={!!actionLoading}
                          onClick={() =>
                            runAction(
                              'stock',
                              runStockPublish,
                              'Lagerpublicering kördes',
                            )
                          }
                        >
                          Kör lagerpublicering nu
                        </Button>
                      </span>
                    </Tooltip>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card
                  elevation={0}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    height: '100%',
                  }}
                >
                  <CardContent>
                    <IntegrationCardHeader
                      icon={<LocalShippingIcon color="primary" />}
                      title="Utleveranser"
                      description="När en order levereras i Viskan ska Fortnox-lagret minskas. Detta flöde är avstängt som standard så att Iizy kan fortsätta hantera utleveranser tills ni byter över."
                      schedules={deliverySchedules}
                    />
                    <Stack spacing={2}>
                      <MetricRow
                        label="Utleveransflöde"
                        help="Inaktiverad = inga utleveranser skickas till Fortnox. Aktiverad = endast leveranser efter starttiden bearbetas."
                      >
                        <Chip
                          size="small"
                          color={deliveryArmed ? 'success' : 'default'}
                          label={
                            deliveryArmed
                              ? 'Aktiverad'
                              : 'Inaktiverad (säkert läge)'
                          }
                        />
                      </MetricRow>
                      <MetricRow
                        label="Starttid (orderdatum)"
                        help="Endast Viskan-utleveranser vars orderdatum (orderDate) är på eller efter denna tidpunkt skickas. Äldre ordrar som uppdateras senare ignoreras."
                      >
                        <Typography variant="body2">
                          {formatDate(mirakel?.delivery.startAfter)}
                        </Typography>
                      </MetricRow>
                      <MetricRow
                        label="Automatisk hämtning"
                        help="Om på: Mirakel hämtar nya leveranser från Viskan enligt schemat ovan (kräver DELIVERY_POLLER_ENABLED på servern)."
                      >
                        <Typography variant="body2">
                          {deliveryPollerEnabled ? 'På' : 'Av'}
                        </Typography>
                      </MetricRow>
                      <MetricRow
                        label="Senast kontrollerad"
                        help="När Mirakel senast hämtade leveranser från Viskan och uppdaterade sin checkpoint."
                      >
                        <Typography variant="body2">
                          {formatDate(mirakel?.delivery.lastCheckpointAt)}
                        </Typography>
                      </MetricRow>
                      <MetricRow
                        label="Väntar i Stocknox"
                        help="Leveranser som mottagits men ännu inte bokförts som utleverans i Fortnox."
                      >
                        <StatusChip
                          value={stocknox?.delivery?.pendingInboxCount || 0}
                          unit="st"
                        />
                      </MetricRow>
                      <MetricRow
                        label="Misslyckade utleveranser"
                        help="Utleveranser som inte kunde bokföras i Fortnox. Kräver felsökning om > 0."
                      >
                        <StatusChip
                          value={stocknox?.delivery?.deadLetterCount || 0}
                          unit="fel"
                        />
                      </MetricRow>
                    </Stack>
                    {!deliveryArmed && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          <strong>Säkert att köra parallellt med Iizy.</strong>{' '}
                          Inget skickas till Fortnox förrän du anger en starttid
                          och aktiverar utleveranser — gör det först när Iizy
                          slutat, annars riskerar ni dubbel utleverans.
                        </Typography>
                      </Alert>
                    )}
                    <Divider sx={{ my: 2 }} />
                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <Tooltip title="Ange från vilken tidpunkt Viskan-leveranser ska skickas till Fortnox.">
                        <span>
                          <Button
                            variant="contained"
                            color={deliveryArmed ? 'inherit' : 'warning'}
                            disabled={!!actionLoading}
                            onClick={handleOpenArmDialog}
                          >
                            {deliveryArmed
                              ? 'Ändra starttid'
                              : 'Aktivera utleveranser'}
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={
                          deliveryArmed
                            ? 'Hämtar nya leveranser från Viskan och skickar till Stocknox/Fortnox nu.'
                            : 'Aktivera utleveranser först.'
                        }
                      >
                        <span>
                          <Button
                            variant="outlined"
                            startIcon={
                              actionLoading === 'delivery' ? (
                                <CircularProgress size={16} />
                              ) : (
                                <SyncAltIcon />
                              )
                            }
                            disabled={!!actionLoading || !deliveryArmed}
                            onClick={() =>
                              runAction(
                                'delivery',
                                runDeliveryPoller,
                                'Utleveranshämtning kördes',
                              )
                            }
                          >
                            Hämta leveranser nu
                          </Button>
                        </span>
                      </Tooltip>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card
                  elevation={0}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    height: '100%',
                  }}
                >
                  <CardContent>
                    <IntegrationCardHeader
                      icon={<FactCheckIcon color="primary" />}
                      title="Kontrollsynk"
                      description="Jämför Fortnox mot Mirakels Viskan-spegel. Avvikelser kan korrigeras automatiskt via reparationsläge."
                      schedules={reconciliationSchedules}
                    />
                    <Stack spacing={2}>
                      <MetricRow
                        label="Senaste kontroll"
                        help="När avstämningen senast kördes i Stocknox."
                      >
                        <Typography variant="body2">
                          {formatDate(stocknox?.reconciliation?.lastRunAt)}
                        </Typography>
                      </MetricRow>
                      <MetricRow
                        label="Resultat senaste körning"
                        help="success = inga problem hittades. failed = något gick fel under körningen."
                      >
                        <Typography variant="body2">
                          {stocknox?.reconciliation?.lastRunStatus === 'success'
                            ? 'Lyckades'
                            : stocknox?.reconciliation?.lastRunStatus ===
                                'failed'
                              ? 'Misslyckades'
                              : stocknox?.reconciliation?.lastRunStatus || '—'}
                        </Typography>
                      </MetricRow>
                      <MetricRow
                        label="Öppna avvikelser"
                        help="Aktuella avvikelser från senaste kontrollsynk. Talet uppdateras (ackumuleras inte) varje gång du kör kontroll."
                      >
                        <StatusChip
                          value={
                            stocknox?.reconciliation?.openDifferenceCount || 0
                          }
                          unit="st"
                        />
                      </MetricRow>
                    </Stack>
                    <Divider sx={{ my: 2 }} />
                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <Tooltip title="Jämför saldon utan att ändra något. Bra för att kontrollera att allt stämmer.">
                        <span>
                          <Button
                            variant="outlined"
                            disabled={!!actionLoading}
                            onClick={handleRunReadOnlyReconciliation}
                          >
                            Kontrollera nu (read-only)
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip title="Försöker åtgärda hittade avvikelser genom att lägga till saknade saldoändringar i publiceringskön.">
                        <span>
                          <Button
                            variant="contained"
                            color="warning"
                            disabled={!!actionLoading}
                            onClick={() =>
                              runAction(
                                'repair',
                                () => runReconciliation(undefined, 'repair'),
                                'Reparationsläge kördes',
                              )
                            }
                          >
                            Reparera avvikelser
                          </Button>
                        </span>
                      </Tooltip>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card
                  elevation={0}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    height: '100%',
                  }}
                >
                  <CardContent>
                    <IntegrationCardHeader
                      icon={<QrCode2Icon color="primary" />}
                      title="Produkt/PLU-synk"
                      description="Skapar saknade artiklar och uppdaterar befintlig produktdata i Fortnox baserat på Viskan/Mirakel-katalogen. Fortnox fortsätter styra lagersaldon."
                      schedules={productSyncSchedules}
                    />
                    <Stack spacing={2}>
                      <MetricRow
                        label="Senaste lyckade synk"
                        help="När Stocknox senast skapade/uppdaterade PLU i Fortnox utan fel."
                      >
                        <Typography variant="body2">
                          {formatDate(
                            stocknox?.productSync?.lastSuccessfulSyncAt,
                          )}
                        </Typography>
                      </MetricRow>
                      <MetricRow
                        label="Misslyckade rader"
                        help="Produkter som inte kunde synkas till Fortnox. OK = 0. Kräver manuell koll om > 0."
                      >
                        <StatusChip
                          value={stocknox?.productSync?.failedCount || 0}
                          unit="st"
                        />
                      </MetricRow>
                    </Stack>
                    <Divider sx={{ my: 2 }} />
                    <Tooltip title="Tvingar en ny PLU-synk nu. Skapar saknade artiklar och uppdaterar produktdata i Fortnox. Kan ta flera minuter.">
                      <span>
                        <Button
                          variant="contained"
                          startIcon={
                            actionLoading === 'product-sync' ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <PlayArrowIcon />
                            )
                          }
                          disabled={!!actionLoading}
                          onClick={() =>
                            runAction(
                              'product-sync',
                              runProductSync,
                              'Produkt/PLU-synk startad',
                            )
                          }
                        >
                          Kör PLU-synk nu
                        </Button>
                      </span>
                    </Tooltip>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Stack>
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        <Dialog
          open={reconciliationPreview != null}
          onClose={() => setReconciliationPreview(null)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Kontrollsynk — avvikelser</DialogTitle>
          <DialogContent dividers>
            {reconciliationPreview?.differences.length === 0 ? (
              <DialogContentText>
                Inga avvikelser hittades mellan Fortnox (Stocknox) och Mirakels
                Viskan-spegel.
              </DialogContentText>
            ) : (
              <>
                <DialogContentText sx={{ mb: 2 }}>
                  {reconciliationPreview?.differences.length} artikel
                  {reconciliationPreview?.differences.length === 1
                    ? ''
                    : 'r'}{' '}
                  med skillnad. Fortnox är källan vid reparation. Körning #
                  {reconciliationPreview?.runId}.
                </DialogContentText>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ maxHeight: 420 }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>PLU</TableCell>
                        <TableCell align="right">Fortnox</TableCell>
                        <TableCell align="right">Viskan (Mirakel)</TableCell>
                        <TableCell align="right">Diff</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[...(reconciliationPreview?.differences ?? [])]
                        .sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff))
                        .map((row) => (
                          <TableRow key={row.pluNo} hover>
                            <TableCell sx={{ fontFamily: 'monospace' }}>
                              {row.pluNo}
                            </TableCell>
                            <TableCell align="right">
                              {row.fortnoxAvailable}
                            </TableCell>
                            <TableCell align="right">
                              {row.viskanBalance}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: 600,
                                color:
                                  row.diff > 0
                                    ? 'success.main'
                                    : row.diff < 0
                                      ? 'error.main'
                                      : 'text.primary',
                              }}
                            >
                              {row.diff > 0 ? `+${row.diff}` : row.diff}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReconciliationPreview(null)}>
              Stäng
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={armDialogOpen}
          onClose={() => setArmDialogOpen(false)}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>
            {deliveryArmed
              ? 'Uppdatera starttid för utleveranser'
              : 'Aktivera utleveranser till Fortnox'}
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              {deliveryArmed
                ? 'Endast utleveranser vars orderdatum (orderDate) är på eller efter den valda tidpunkten skickas till Fortnox. Äldre ordrar som uppdateras senare bearbetas inte.'
                : 'Detta aktiverar utleveransflödet utan att bearbeta historiska ordrar. Gör detta först när Iizy inte längre ska hantera utleveranser, annars riskerar ni dubbel utleverans.'}
            </DialogContentText>
            <TextField
              label="Startdatum och tid"
              type="datetime-local"
              value={armStartAt}
              onChange={(event) => setArmStartAt(event.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Ordrar med orderdatum före denna tidpunkt ignoreras."
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setArmDialogOpen(false)}>Avbryt</Button>
            <Button
              variant="contained"
              color="warning"
              onClick={handleArmDeliveryPoller}
              disabled={!armStartAt}
            >
              {deliveryArmed ? 'Spara starttid' : 'Aktivera'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </PermissionWrapper>
  );
};

export default Integrations;
